import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Download,
  FileText,
  Loader2,
  Pencil,
  Printer,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ApiError } from '@/lib/api';
import {
  deleteCrossExamPlan,
  getCrossExamPlan,
  planExportUrl,
  recheckPlanTraps,
  updateCrossExamPlan,
  type CrossExamPlan,
  type PlanStatus,
  type QuestionStep,
} from '@/lib/api-client';

// Phase 10.6 step 4 — cross-examination plan: the single admission sought, a
// numbered sequence of short leading questions building to it, the exhibit or
// prior statement each question rests on, and the traps (what NOT to ask).
// The print view is formatted for a bar table: one page per witness, large
// type, questions numbered (see the print CSS at the bottom).

const STATUS_FLOW: PlanStatus[] = ['draft', 'rehearsed', 'live', 'complete'];
const STATUS_LABEL: Record<PlanStatus, string> = {
  draft: 'Draft',
  rehearsed: 'Rehearsed',
  live: 'Live',
  complete: 'Complete',
};

const RISK_STYLE: Record<QuestionStep['riskLevel'], string> = {
  safe: 'border-emerald-700/50 bg-emerald-950/30 text-emerald-300',
  medium: 'border-amber-700/50 bg-amber-950/30 text-amber-300',
  high: 'border-red-800/60 bg-red-950/30 text-red-300',
};

export function CrossExamPlanPage() {
  const { planId = '' } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<CrossExamPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingQ, setEditingQ] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setPlan(await getCrossExamPlan(planId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the plan.');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(input: Parameters<typeof updateCrossExamPlan>[1]): Promise<void> {
    if (!plan) return;
    setError(null);
    setBusy(true);
    try {
      setPlan(await updateCrossExamPlan(plan.id, input));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the change.');
    } finally {
      setBusy(false);
    }
  }

  async function recheck(): Promise<void> {
    if (!plan) return;
    setError(null);
    setBusy(true);
    try {
      await recheckPlanTraps(plan.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Trap re-check failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removePlan(): Promise<void> {
    if (!plan) return;
    try {
      await deleteCrossExamPlan(plan.id);
      navigate(`/matters/${plan.matterId}/witnesses`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the plan.');
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-stone-950 px-6 py-10 text-stone-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </main>
    );
  }
  if (!plan) {
    return (
      <main className="min-h-screen bg-stone-950 px-6 py-10">
        <p className="text-sm text-red-400">{error ?? 'Plan not found.'}</p>
      </main>
    );
  }

  const nextStatus = STATUS_FLOW[STATUS_FLOW.indexOf(plan.status) + 1];
  const questions = plan.questionSequence ?? [];

  return (
    <main className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6 print:bg-white print:px-0 print:py-0">
      <div className="mx-auto w-full max-w-3xl space-y-5 print:max-w-none print:space-y-4">
        {/* Screen-only header controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <Link
            to={`/matters/${plan.matterId}/witnesses`}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Witnesses
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${
                plan.status === 'live'
                  ? 'border-red-800/60 bg-red-950/30 text-red-300'
                  : plan.status === 'rehearsed'
                    ? 'border-emerald-700/50 bg-emerald-950/30 text-emerald-300'
                    : 'border-stone-700 bg-stone-900 text-stone-400'
              }`}
            >
              {STATUS_LABEL[plan.status]}
            </span>
            {nextStatus ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void patch({ status: nextStatus })}
                className="inline-flex items-center gap-1 rounded-md border border-stone-700 px-2.5 py-1 text-[11px] uppercase tracking-[0.15em] text-stone-300 hover:border-amber-700/60 disabled:opacity-40"
              >
                <Check className="h-3.5 w-3.5" /> Mark {STATUS_LABEL[nextStatus]}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1 rounded-md border border-amber-700/50 bg-amber-950/30 px-2.5 py-1 text-[11px] uppercase tracking-[0.15em] text-amber-300 hover:bg-amber-900/30"
            >
              <Printer className="h-3.5 w-3.5" /> Print for bar table
            </button>
            <a
              href={planExportUrl(plan.id, 'pdf')}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:text-amber-400"
            >
              <Download className="h-3.5 w-3.5" /> PDF
            </a>
            <a
              href={planExportUrl(plan.id, 'docx')}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:text-amber-400"
            >
              <Download className="h-3.5 w-3.5" /> DOCX
            </a>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-stone-600 hover:text-red-400"
              aria-label="Delete plan"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {error ? <p className="text-sm text-red-400 print:hidden">{error}</p> : null}
        {plan.citationsFlagged > 0 ? (
          <p className="flex items-center gap-2 text-xs text-amber-300 print:hidden">
            <AlertTriangle className="h-3.5 w-3.5" />
            {plan.citationsFlagged} legal citation{plan.citationsFlagged === 1 ? '' : 's'} in this
            plan could not be verified against a retrieved source — check before relying on them.
          </p>
        ) : null}

        {/* Printable body */}
        <div className="print-sheet space-y-5 print:space-y-4">
          <header>
            <h1 className="font-display text-2xl italic text-stone-100 print:text-3xl print:not-italic print:font-bold print:text-black">
              {plan.witness?.name ?? 'Witness'}
            </h1>
            <p className="text-sm text-stone-500 print:text-base print:text-black">
              {plan.matter?.name}
              {plan.matter?.reference ? ` · ${plan.matter.reference}` : ''} · ~
              {plan.estimatedDuration} min
            </p>
          </header>

          <Card
            variant="default"
            padding="md"
            className="border-amber-700/50 bg-amber-950/20 print:border-2 print:border-black print:bg-white"
          >
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400 print:text-sm print:font-bold print:text-black">
              The admission I need
            </p>
            <p className="mt-1 text-base text-stone-100 print:text-xl print:font-semibold print:text-black">
              {plan.objective || '(not recorded)'}
            </p>
          </Card>

          {plan.opening ? (
            <section>
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 print:text-sm print:font-bold print:text-black">
                Opening
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-stone-300 print:text-lg print:text-black">
                {plan.opening}
              </p>
            </section>
          ) : null}

          <section className="space-y-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 print:text-sm print:font-bold print:text-black">
              Questions
            </p>
            {questions.map((q, i) => (
              <div
                key={i}
                className="rounded-md border border-stone-800/60 bg-stone-900/40 p-3 print:break-inside-avoid print:rounded-none print:border-0 print:border-b print:border-stone-300 print:bg-white print:p-0 print:pb-3"
              >
                {editingQ === i ? (
                  <QuestionEditor
                    question={q}
                    busy={busy}
                    onCancel={() => setEditingQ(null)}
                    onSave={async (updated) => {
                      const next = questions.map((qq, j) => (j === i ? updated : qq));
                      await patch({ questionSequence: next });
                      setEditingQ(null);
                    }}
                  />
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-stone-100 print:text-xl print:leading-snug print:text-black">
                        <span className="mr-2 font-mono text-amber-500 print:font-bold print:text-black">
                          {q.sequenceNumber ?? i + 1}.
                        </span>
                        {q.questionText}
                      </p>
                      <span className="flex shrink-0 items-center gap-2 print:hidden">
                        <span
                          className={`rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.1em] ${RISK_STYLE[q.riskLevel] ?? RISK_STYLE.medium}`}
                        >
                          {q.riskLevel}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditingQ(i)}
                          className="text-stone-600 hover:text-amber-400"
                          aria-label={`Edit question ${i + 1}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-stone-500 print:text-sm print:text-stone-700">
                      Expect: {q.expectedAnswer || '—'}
                    </p>
                    {q.supportingExhibit ? (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-stone-400 print:text-sm print:font-medium print:text-black">
                        <FileText className="h-3 w-3 print:hidden" /> Rests on: {q.supportingExhibit}
                      </p>
                    ) : null}
                    {q.escapeRoutes?.length ? (
                      <details className="mt-1 text-xs text-stone-500 print:hidden">
                        <summary className="cursor-pointer">
                          {q.escapeRoutes.length} escape route{q.escapeRoutes.length === 1 ? '' : 's'}
                        </summary>
                        <ul className="mt-1 space-y-1 pl-4">
                          {q.escapeRoutes.map((e, j) => (
                            <li key={j}>
                              <span className="text-stone-400">If “{e.dodge}”</span> → {e.followUp}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </>
                )}
              </div>
            ))}
            {questions.length === 0 ? (
              <p className="text-sm text-stone-600">No questions in this plan.</p>
            ) : null}
          </section>

          {plan.trapsToAvoid?.length ? (
            <section className="print:break-inside-avoid">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.2em] text-red-400 print:text-sm print:font-bold print:text-black">
                  Do not ask
                </p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void recheck()}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:text-amber-400 print:hidden"
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  Re-check traps
                </button>
              </div>
              <div className="mt-2 space-y-2 rounded-md border border-red-900/40 bg-red-950/10 p-3 print:rounded-none print:border-2 print:border-black print:bg-white">
                {plan.trapsToAvoid.map((t, i) => (
                  <div key={i} className="text-sm print:break-inside-avoid">
                    <p className="text-stone-200 print:text-base print:font-semibold print:text-black">
                      ✕ {t.description}
                    </p>
                    <p className="text-xs text-stone-500 print:text-sm print:text-stone-700">
                      Instead: {t.alternativeApproach}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {plan.closingMove ? (
            <section className="print:break-inside-avoid">
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 print:text-sm print:font-bold print:text-black">
                Closing move
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-stone-300 print:text-lg print:text-black">
                {plan.closingMove}
              </p>
            </section>
          ) : null}
        </div>
      </div>

      {confirmDelete ? (
        <ConfirmDialog
          title="Delete this plan?"
          message="The plan, its questions and trap notes will be permanently removed."
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            setConfirmDelete(false);
            void removePlan();
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}

      {/* Bar-table print sheet: one witness per page, large type. */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 18mm 16mm; }
          body { background: white !important; }
          .print-sheet { page-break-after: always; font-size: 14pt; }
        }
      `}</style>
    </main>
  );
}

function QuestionEditor({
  question,
  busy,
  onSave,
  onCancel,
}: {
  question: QuestionStep;
  busy: boolean;
  onSave: (q: QuestionStep) => Promise<void>;
  onCancel: () => void;
}) {
  const [text, setText] = useState(question.questionText);
  const [expected, setExpected] = useState(question.expectedAnswer);
  const [exhibit, setExhibit] = useState(question.supportingExhibit ?? '');

  const inputClass =
    'w-full rounded-md border border-stone-800 bg-stone-900 px-2 py-1.5 text-sm text-stone-200 focus:border-amber-700/60 focus:outline-none';

  return (
    <div className="space-y-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        className={inputClass}
        aria-label="Question text"
      />
      <input
        value={expected}
        onChange={(e) => setExpected(e.target.value)}
        placeholder="Expected answer"
        className={inputClass}
        aria-label="Expected answer"
      />
      <input
        value={exhibit}
        onChange={(e) => setExhibit(e.target.value)}
        placeholder="Exhibit / prior statement this rests on"
        className={inputClass}
        aria-label="Supporting exhibit"
      />
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void onSave({
              ...question,
              questionText: text.trim(),
              expectedAnswer: expected.trim(),
              supportingExhibit: exhibit.trim() || null,
            })
          }
          className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-stone-950 hover:bg-amber-700 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>
    </div>
  );
}
